/* eslint-disable camelcase */
import React, { useState, useMemo, useEffect } from 'react';
import { useFragment } from '@apollo/client';

/* TYPES */
import {
    IDeliveryGowfoodDaysRowProps,
    IDeliveryTimeIntervalsProps,
    TDeliveryZoneRecord,
} from './deliveryZoneTimeAndPrice.types';

/* UTILS  */
import { getDeliveryZoneRecord, parsDeliveryZoneId } from './deliveryZoneTimeAndPrice.utils';

/* REQ */
import { DELIVERY_ZONE_REGION_NAME, DELIVERY_ZONE_ADDRESS_PRICE } from './graphql/deliveryZoneTimeAndPrice.graphql';

/* STYLES */
import css from './deliveryZoneTimeAndPrice.module.scss';


function DeliveryPriceRow() {
    const { data: address } = useFragment<any>({
        fragment: DELIVERY_ZONE_ADDRESS_PRICE,
        from: { __ref: 'address:session_address' },
    });

    const { delivery_price_low, delivery_price_other } = address || {};

    if (!Number.isFinite(delivery_price_low) || !Number.isFinite(delivery_price_other)) {
        return null;
    }

    const priceForCheapOrder = delivery_price_low + delivery_price_other;
    const priceForExpensiveOrder = delivery_price_other;

    const isFreeForExpensiveOrder = priceForExpensiveOrder === 0;

    return (
        <div className={css.deliveryPriceRow}>
            <div className={css.deliveryPriceRowTitle}>
                {isFreeForExpensiveOrder
                    ? 'Бесплатная доставка от 2 400 ₽'
                    : `Доставка от ${priceForExpensiveOrder} ₽`}
            </div>

            <div className={css.deliveryPriceRowValue}>
                {`${priceForCheapOrder} ₽ — при заказе менее 2 400 ₽`}
            </div>

            {isFreeForExpensiveOrder ? (
                <div className={css.deliveryPriceRowValue}>
                    при заказе от 2 400 ₽ — бесплатно
                </div>
            ) : (
                <div className={css.deliveryPriceRowValue}>
                    ваш адрес в зоне платной доставки
                </div>
            )}
        </div>
    );
}

function DeliveryGowfoodDaysRow(props: IDeliveryGowfoodDaysRowProps) {
    const {
        growFoodDaysList = [],
    } = props;

    if (!growFoodDaysList.length) {
        return null;
    }

    return (
        <div className={css.deliveryGowfoodDaysRowRoot}>
            <div className={css.deliveryGowfoodDaysRowTitle}>
                Время доставки
            </div>
            <div className={css.deliveryGowfoodDaysRowDays}>
                {growFoodDaysList.map((dayText) => (
                    <div
                        className={css.deliveryGowfoodDaysRowDaysItem}
                        key={dayText}
                    >
                        {dayText}
                    </div>
                ))}
            </div>
        </div>
    );
}

function DeliveryTimeIntervals(props: IDeliveryTimeIntervalsProps) {
    const {
        morningRange = 0,
        eveningRange = 0,
    } = props;
    return (
        <div className={css.deliveryTimeIntervalsRoot}>
            {!!morningRange && (
                <div className={css.deliveryTimeIntervalsSection}>
                    <div className={css.deliveryTimeIntervalsSectionTime}>
                        Утро, 06:00—13:00
                    </div>
                    <div className={css.deliveryTimeIntervalsSectionBorders}>
                        {morningRange}
                        -часовые интервалы доставки
                    </div>
                </div>
            )}
            {!!eveningRange && (
                <div className={css.deliveryTimeIntervalsSection}>
                    <div className={css.deliveryTimeIntervalsSectionTime}>
                        Вечер, 18:00—00:00
                    </div>
                    {eveningRange !== 6 && (
                        <div className={css.deliveryTimeIntervalsSectionBorders}>
                            {eveningRange}
                            -часовые интервалы доставки
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export function DeliveryZoneTimeAndPrice({ clearCityInputCounter }: any) {
    const [areDaysHidden, setAreDaysHidden] = useState(false);
    /* DAC */
    const { data } = useFragment<any>({
        fragment: DELIVERY_ZONE_REGION_NAME,
        from: {
            __ref: 'intervals:user-session',
        },
    });
    const { data: address } = useFragment<any>({
        fragment: DELIVERY_ZONE_ADDRESS_PRICE,
        from: {
            __ref: 'address:session_address',
        },
    });

    /* SELECTORS */
    const zoneId = useMemo(
        () => {
            setAreDaysHidden(false);
            return parsDeliveryZoneId(data?.region_name);
        },
        [data?.region_name],
    );

    const { days, eveningRange, morningRange } = useMemo<TDeliveryZoneRecord>(
        () => getDeliveryZoneRecord(zoneId),
        [zoneId],
    );

    useEffect(
        () => {
            setAreDaysHidden(true);
        },
        [clearCityInputCounter],
    );

    if (!Number.isFinite(address?.delivery_price_low) || !Number.isFinite(address?.delivery_price_other)) {
        return null;
    }

    return (
        <div className={css.deliveryZoneTimeAndPriceRoot}>
            <DeliveryPriceRow />
            <DeliveryGowfoodDaysRow
                growFoodDaysList={areDaysHidden ? [] : days}
            />
            {!areDaysHidden && (
                <DeliveryTimeIntervals
                    eveningRange={eveningRange}
                    morningRange={morningRange}
                />
            )}
        </div>
    );
}
